<template>
  <!-- Innovadis custom anchor link -->
  <div v-if="!featured" id="all-properties"></div>
  <div
    v-if="showPropertiesBlock || (productReviewsEnabled && product.rating) || showVendor"
    class="properties lg:mt-8"
    :class="{ 'xl:max-w-[50%]': !featured }"
  >
    <VcWidget
      v-if="showPropertiesBlock"
      class="properties border-none shadow-none bg-transparent"
      :class="{ 'properties-all': !featured, 'properties-featured': featured }"
    >
      <template #after>
        <VcButton
          v-if="groupedProperties.length > 1"
          color="secondary"
          size="xs"
          variant="outline"
          @click="toggleWidgets"
        >
          {{ allExpanded ? $t("common.buttons.show_less") : $t("common.buttons.expand_all") }}
        </VcButton>
      </template>

      <div class="properties__groups">
        <VcTypography v-if="!featured" tag="h2" variant="h3" class="text-[--colo-body-text]">
          {{ model.title || $t("shared.catalog.product_details.properties_block_title") }}</VcTypography
        >
        <VcWidget
          v-for="(item, index) in groupedProperties"
          :key="index"
          size="xs"
          collapsible
          :collapsed="collapsedStates[index]"
          @toggle-collapse="($event) => (collapsedStates[index] = $event)"
          class="border-none shadow-none bg-transparent"
        >
          <template v-if="item.group" #title>
            <span class="normal-case"> {{ item.group?.name }} </span>
          </template>

          <div class="properties__group">
            <VcCollapsibleContent on-gray :max-height="groupedProperties.length === 1 ? '18.75rem' : 'none'">
              <div v-for="property in item.properties" :key="property.id" class="properties__prop">
                <div class="properties__label text-[--color-body-text] whitespace-nowrap">
                  {{
                    $t(`shared.catalog.product_details.properties.property_${property.label}`) ===
                    `shared.catalog.product_details.properties.property_${property.label}`
                      ? property.label
                      : $t(`shared.catalog.product_details.properties.property_${property.label}`)
                  }}
                </div>

                <div class="properties__value text-right font-semibold">
                  <span v-if="isHTML(property)">
                    <VcMarkdownRender :src="String(property.value)" />
                  </span>

                  <!-- Innovadis custom: check on property CUSTOM_PROPERTY_BRAND -->
                  <span v-else-if="property.name === CUSTOM_PROPERTY_BRAND && brandUrl">
                    <a :href="brandUrl" class="text-[--link-color] hover:text-[--link-hover-color]">
                      {{ property.value }}
                    </a>
                  </span>

                  <span v-else>
                    {{ property.value }}
                  </span>
                </div>
              </div>
            </VcCollapsibleContent>
          </div>
        </VcWidget>
      </div>
    </VcWidget>

    <div v-if="(productReviewsEnabled && product.rating) || showVendor" class="mt-5 space-y-4">
      <!-- Rating -->
      <VcProperty
        v-if="productReviewsEnabled && product.rating"
        :label="$t('shared.catalog.product_card.product_rating')"
        class="text-base"
      >
        <ProductRating :rating="product.rating" />
      </VcProperty>

      <!-- Vendor -->
      <VcProperty v-if="showVendor" :label="$t('shared.catalog.product_details.vendor_label')" class="text-base">
        <Vendor :vendor="product.vendor!" with-rating />
      </VcProperty>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { PropertyType } from "@/core/api/graphql/types";
import { useThemeContext } from "@/core/composables";
import { globals } from "@/core/globals";
import { useModuleSettings } from "@/core/composables/useModuleSettings";
import { useRouter } from "vue-router";
import { Vendor } from "@/shared/catalog";
import { getPropertiesGroupedByName, getGroupedAndSortedProperties } from "@/core/utilities";
import {
  MODULE_ID as CUSTOMER_REVIEWS_MODULE_ID,
  ENABLED_KEY as CUSTOMER_REVIEWS_ENABLED_KEY,
} from "@/modules/customer-reviews/constants";
import { PropertyValueTypes } from "@/modules/quotes/api/graphql/types";
import {
  CUSTOM_PROPERTIES_FOR_PDP_INFO,
  CUSTOM_PROPERTIES_PDP_LIST_EXCLUDES,
  CUSTOM_PROPERTY_BRAND,
} from "@/shared/common/constants";
import ProductRating from "@/modules/customer-reviews/components/product-rating.vue";
import type { Product, Property } from "@/core/api/graphql/types";

interface IProps {
  product: Product;
  featured: boolean;
  model: {
    title?: string;
    hidden?: boolean;
  };
}

const props = withDefaults(defineProps<IProps>(), {
  featured: false,
});

const collapsedStates = ref<boolean[]>([]);
const router = useRouter();
const { themeContext } = useThemeContext();
const { isEnabled } = useModuleSettings(CUSTOMER_REVIEWS_MODULE_ID);
const productReviewsEnabled = isEnabled(CUSTOMER_REVIEWS_ENABLED_KEY);

const properties = computed(() => {
  const allProps = Object.values(getPropertiesGroupedByName(props.product.properties ?? [], PropertyType.Product));

  // Innovadis custom: add variant properties
  let allVariationProps: Property[] = [];
  if (!props.product.hasVariations) {
    allVariationProps = Object.values(
      getPropertiesGroupedByName(props.product.properties ?? [], PropertyType.Variation),
    );
  }

  // Safely concatenate both arrays
  const combinedProps = [...allProps, ...allVariationProps];

  // Innovadis custom: filter out unwanted properties and format labels (start with capital, replace underscores by spaces)
  const filteredProps = combinedProps
    .filter((prop) => !CUSTOM_PROPERTIES_PDP_LIST_EXCLUDES.includes(prop.name))
    .map((prop) => ({
      ...prop,
      label: prop.label
        ? prop.label.replace(/_/g, " ").replace(/^./, (char) => char.toUpperCase()) // Capitalize only the first character
        : prop.label,
    }));

  return props.featured
    ? filteredProps.filter(({ name }) => CUSTOM_PROPERTIES_FOR_PDP_INFO.includes(name))
    : filteredProps;
});

const groupedProperties = computed(() => getGroupedAndSortedProperties(properties.value));

const showVendor = computed(
  () => themeContext.value?.settings?.vendor_enabled && !props.product.hasVariations && props.product.vendor,
);
const showPropertiesBlock = computed(() => !props.model.hidden && (properties.value.length || showVendor.value));
const allExpanded = computed(() => collapsedStates.value.every((val) => val === false));

// Innovadis custom: compute brands base path for brand link
const brandsBasePath = computed(() => {
  const resolvedBrandsRoute = router.resolve({
    name: "Brands",
    params: { locale: globals.cultureName?.toLowerCase() },
  });

  return resolvedBrandsRoute.path.replace(/\/$/, "");
});

// Innovadis custom: compute brand URL based on CUSTOM_PROPERTY_BRAND
const brandUrl = computed(() => {
  const brandProperty = props.product.properties?.find((prop) => prop.name === CUSTOM_PROPERTY_BRAND);
  if (!brandProperty?.value || typeof brandProperty.value !== "string") {
    return null;
  }

  const brandSlug = encodeURIComponent(String(brandProperty.value));
  if (!brandSlug) {
    return null;
  }

  return `${brandsBasePath.value}/${brandSlug}`;
});

function toggleWidgets() {
  if (!allExpanded.value) {
    collapsedStates.value = groupedProperties.value.map(() => false);
  } else {
    collapsedStates.value = groupedProperties.value.map((_, index) => index !== 0);
  }
}

function initCollapsedStates() {
  collapsedStates.value = groupedProperties.value.map((_, index) => index !== 0);
}

function isHTML(property: Property): boolean {
  return (property.propertyValueType as PropertyValueTypes) === PropertyValueTypes.Html;
}

onMounted(() => {
  initCollapsedStates();
});
</script>

<style lang="scss">
.properties {
  &__groups {
    @apply space-y-3;
  }

  &__prop {
    @apply flex items-stretch text-sm rounded border-b border-[--color-gray-line] gap-2;
  }

  &__label {
    @apply flex-none min-w-32 py-2.5 ps-0 text-[--color-body-text] break-words;
  }

  &__value {
    @apply grow py-2.5 px-2 text-neutral-950;

    word-break: break-word;
  }
}

.properties-all {
  @apply px-3 md:px-0;

  .vc-widget__slot {
    @apply px-0;
  }
}
</style>
